import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MarketplaceCard } from "@/components/ui/marketplace-card";
import { ReactNode } from "react";

interface BuyerRequestCardProps {
  id: string;
  niche: string;
  drMin: number;
  daMin: number;
  budgetMin: number;
  budgetMax: number;
  linkType: string;
  notes: string;
  expiresAt: string;
  createdAt: string;
  userId: string;
  userName?: string;
  userAvatar?: string | null;
}

const BuyerRequestCard = ({
  id,
  niche,
  drMin,
  daMin,
  budgetMin,
  budgetMax,
  linkType,
  notes,
  expiresAt,
  createdAt,
  userId,
  userName = "Anonymous",
  userAvatar,
}: BuyerRequestCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  const requestUrl = `/buyer-requests/${id}`;
  
  // Title component with link
  const titleComponent = (
    <Link to={requestUrl} className="hover:underline">
      <h3 className="text-sm font-semibold min-h-[2.5rem] line-clamp-2">
        {niche} - {linkType || "Any"} links
      </h3>
    </Link>
  );
  
  // Subtitle with posting date
  const subtitleComponent = (
    <div className="flex items-center">
      <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
      <span>Posted {formatDate(createdAt)}</span>
    </div>
  );
  
  // Metrics for the request
  const metrics = [
    {
      value: `${drMin}+`,
      label: "DR"
    },
    {
      value: `${daMin}+`,
      label: "DA"
    },
    {
      value: (
        <>
          <Tag className="h-3 w-3 inline mr-0.5" />
          {linkType || "Any"}
        </>
      ),
      label: "Type",
      tooltip: linkType || "Any Link Type"
    }
  ];
  
  // Price display section
  const priceSection = (
    <div className="flex items-center justify-between mb-3 w-full overflow-hidden mt-2">
      <span className="text-base font-bold">
        ${budgetMin} - ${budgetMax}
      </span>
      <Badge variant="outline" className="text-brand-purple border-brand-purple text-xs h-5 px-1.5 flex-shrink-0">
        Budget
      </Badge>
    </div>
  );
  
  // Bottom info with expiry and user - improved styling
  const bottomInfoComponent = (
    <div className="flex items-center justify-between gap-2 hover:bg-gray-50 p-1 rounded-md mt-2 w-full overflow-hidden">
      <div className="flex items-center text-gray-500 text-xs min-w-0">
        <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">Expires {formatDate(expiresAt)}</span>
      </div>
      
      <Link to={`/users/${userId}`} className="flex items-center text-gray-600 hover:text-brand-blue gap-1 ml-auto">
        <Avatar className="h-5 w-5 flex-shrink-0">
          <AvatarImage src={userAvatar || ""} alt={userName} />
          <AvatarFallback>{userName.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="text-xs font-medium truncate max-w-[70px]">{userName}</span>
      </Link>
    </div>
  );
  
  return (
    <MarketplaceCard
      title={titleComponent}
      subtitle={subtitleComponent}
      description={notes}
      metrics={metrics}
      metricsColumns={3}
      bottomInfo={bottomInfoComponent}
      linkTo={requestUrl}
      actionLabel="Respond to Request"
    >
      {priceSection}
    </MarketplaceCard>
  );
};

export default BuyerRequestCard;
